const API_URL = process.env.REACT_APP_API_URL;

/**
 * Extract the best error message from a ProblemDetail response body.
 * Prefers the detail field (which now contains user-friendly messages from Meta API),
 * optionally prefixed with the title for context.
 */
function extractErrorMessage(errorBody, fallback) {
  if (!errorBody) return fallback;
  const detail = errorBody.detail;
  const title = errorBody.title;
  if (detail && title && title !== 'Meta API Error') {
    return `${title}: ${detail}`;
  }
  return detail || fallback;
}

/**
 * Build the create/update request body, including the dynamic-flow fields
 * (endpointUri, categories) and the local-only agentMeta only when they are
 * present so static flows keep sending the lean {name, description, flowJson} body.
 */
function buildFlowBody(flowData) {
  const body = {
    name: flowData.name,
    description: flowData.description,
    flowJson: flowData.flowJson,
    // flowType is a defined enum (STATIC | DYNAMIC); always send it, defaulting
    // to STATIC so flows without an explicit type stay static.
    flowType: flowData.flowType || 'STATIC'
  };
  if (flowData.endpointUri) {
    body.endpointUri = flowData.endpointUri;
  }
  if (Array.isArray(flowData.categories) && flowData.categories.length > 0) {
    body.categories = flowData.categories;
  }
  // agentMeta is local-only metadata describing the flow as an AI-agent tool;
  // the backend persists it but never forwards it to Meta.
  if (flowData.agentMeta && Object.keys(flowData.agentMeta).length > 0) {
    body.agentMeta = flowData.agentMeta;
  }
  return body;
}

/**
 * Service for managing flows via backend API.
 * Handles all flow CRUD operations and WhatsApp integration.
 */
class FlowService {

  /**
   * Create a new flow.
   *
   * @param {string} envId Environment ID
   * @param {object} flowData Flow data {name, description, flowJson}
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<object>} Created flow
   */
  async createFlow(envId, flowData, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows`,
      {
        method: 'POST',
        body: JSON.stringify(buildFlowBody(flowData))
      }
    );

    if (response.status === 403) {
      throw new Error('Only organisation owners can create flows');
    }

    if (response.status === 409) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'A flow with this name already exists'));
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to create flow'));
    }

    return await response.json();
  }

  /**
   * List all flows for an environment.
   *
   * @param {string} envId Environment ID
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<Array>} List of flows
   */
  async listFlows(envId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch flows');
    }

    return await response.json();
  }

  /**
   * Get a single flow by ID.
   *
   * @param {string} envId Environment ID
   * @param {string} flowId Flow ID
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<object>} Flow
   */
  async getFlow(envId, flowId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}`
    );

    if (response.status === 404) {
      throw new Error('Flow not found');
    }

    if (!response.ok) {
      throw new Error('Failed to fetch flow');
    }

    return await response.json();
  }

  /**
   * Check whether a flow's local JSON has drifted from the version stored on WhatsApp.
   * Returns { inSync, remoteAvailable, remoteJson } (remoteJson present only when out of sync).
   */
  async checkDrift(envId, flowId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/drift`
    );

    if (!response.ok) {
      throw new Error('Failed to check for WhatsApp changes');
    }

    return await response.json();
  }

  /**
   * Update an existing flow.
   *
   * @param {string} envId Environment ID
   * @param {string} flowId Flow ID
   * @param {object} flowData Flow data {name, description, flowJson}
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<object>} Updated flow
   */
  async updateFlow(envId, flowId, flowData, authenticatedFetch) {
    // snapshot=true records a version-history entry (manual saves only, auto-save omits it).
    const query = flowData.snapshot ? '?snapshot=true' : '';
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}${query}`,
      {
        method: 'PUT',
        body: JSON.stringify(buildFlowBody(flowData))
      }
    );

    if (response.status === 403) {
      throw new Error('Only organisation owners can update flows');
    }

    if (response.status === 409) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'A flow with this name already exists'));
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to update flow'));
    }

    return await response.json();
  }

  /**
   * Delete a flow (soft delete - archive).
   *
   * @param {string} envId Environment ID
   * @param {string} flowId Flow ID
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<{removedFromWhatsApp: boolean}>}
   */
  async deleteFlow(envId, flowId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}`,
      { method: 'DELETE' }
    );

    if (response.status === 403) {
      throw new Error('Only organisation owners can delete flows');
    }

    if (!response.ok) {
      throw new Error('Failed to delete flow');
    }

    // WhatsApp keeps published flows permanently, so the backend reports whether the flow was
    // actually removed from the WABA or only archived locally.
    return response.json().catch(() => ({ removedFromWhatsApp: false }));
  }

  /**
   * Deploy a flow to WhatsApp.
   *
   * @param {string} envId Environment ID
   * @param {string} flowId Flow ID
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<object>} Deploy response with WhatsApp flow ID
   */
  async deployFlow(envId, flowId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/deploy`,
      { method: 'POST' }
    );

    if (response.status === 403) {
      throw new Error('Only organisation owners can deploy flows');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to deploy flow'));
    }

    return await response.json();
  }

  /**
   * Duplicate a flow within the same environment.
   *
   * @param {string} envId Environment ID
   * @param {string} flowId Flow ID to duplicate
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<object>} The new duplicated flow
   */
  async duplicateFlow(envId, flowId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/duplicate`,
      { method: 'POST' }
    );
    if (response.status === 403) {
      throw new Error('Only organisation owners can duplicate flows');
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to duplicate flow'));
    }
    return await response.json();
  }

  /**
   * Move a flow to a different environment in the same organisation.
   *
   * @param {string} envId Current environment ID
   * @param {string} flowId Flow ID
   * @param {string} targetEnvironmentId Destination environment ID
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<object>} The moved flow
   */
  async moveFlow(envId, flowId, targetEnvironmentId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/move`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ targetEnvironmentId }) }
    );
    if (response.status === 403) {
      throw new Error('Only organisation owners can move flows');
    }
    if (response.status === 409) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'A flow with this name already exists in the target environment'));
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to move flow'));
    }
    return await response.json();
  }

  /**
   * List a flow's saved version history (newest first).
   *
   * @param {string} envId Environment ID
   * @param {string} flowId Flow ID
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<Array>} Version entries
   */
  async getVersions(envId, flowId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/versions`
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to load version history'));
    }
    return await response.json();
  }

  /**
   * Restore a flow to a previous version. The current state is snapshotted first.
   *
   * @param {string} envId Environment ID
   * @param {string} flowId Flow ID
   * @param {number} versionNumber Version to restore
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<object>} Restored flow
   */
  async restoreVersion(envId, flowId, versionNumber, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/versions/${versionNumber}/restore`,
      { method: 'POST' }
    );
    if (response.status === 403) {
      throw new Error('Only organisation owners can restore versions');
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to restore version'));
    }
    return await response.json();
  }

  /**
   * Report our builder's own validation verdict for a flow (ADR-0006). Called when a flow finishes
   * syncing INVALID so the backend discrepancy corpus knows whether our builder also flagged it.
   * Best-effort: failures are swallowed (this is telemetry, not a user action).
   *
   * @param {string} envId Environment ID
   * @param {string} flowId Flow ID
   * @param {number} builderErrorCount Number of issues our builder flagged (0 = we thought it valid)
   * @param {function} authenticatedFetch Authenticated fetch function
   */
  async reportValidationFeedback(envId, flowId, builderErrorCount, authenticatedFetch) {
    try {
      await authenticatedFetch(
        `${API_URL}/api/environments/${envId}/flows/${flowId}/validation-feedback`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ builderErrorCount }),
        }
      );
    } catch (error) {
      console.warn('Failed to report validation feedback:', error);
    }
  }

  /**
   * Publish a flow on WhatsApp.
   *
   * @param {string} envId Environment ID
   * @param {string} flowId Flow ID
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<object>} Published flow
   */
  async publishFlow(envId, flowId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/publish`,
      { method: 'POST' }
    );

    if (response.status === 403) {
      throw new Error('Only organisation owners can publish flows');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to publish flow'));
    }

    return await response.json();
  }

  /**
   * Get this flow onto the phone of the person who built it.
   *
   * <p>Answers with a code and a wa.me link rather than sending: they message FlowGate first,
   * which is what WhatsApp requires outside a template and the reason this can never deliver
   * anything unsolicited. A demo's flow is published on FlowGate's shared number as a side
   * effect; anybody else's is answered from their own number exactly as it stands, `draft` true
   * when it is not published yet.
   *
   * @param {string} envId Environment ID
   * @param {string} flowId Flow ID
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<{code: string, phoneNumber: string, waMeUrl: string, expiresAt: string, message: string, draft: boolean}>}
   */
  async shareToPhone(envId, flowId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/share-to-phone`,
      { method: 'POST' }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Could not get this flow onto a phone'));
    }

    return await response.json();
  }

  /**
   * The Live data section of a flow's Connections panel: whether FlowGate answers the flow
   * (a booking flow), sends it on to the owner's server, or neither, and how the last call went.
   */
  async getLiveData(envId, flowId, authenticatedFetch) {
    return this.liveDataCall(envId, flowId, '', { method: 'GET' }, authenticatedFetch,
      'Failed to load this flow\'s live data');
  }

  /** Send this flow's live data to the owner's server (HTTPS, public address). */
  async forwardLiveData(envId, flowId, url, authenticatedFetch) {
    return this.liveDataCall(envId, flowId, '', { method: 'PUT', body: JSON.stringify({ url }) },
      authenticatedFetch, 'Failed to save your server\'s address');
  }

  async stopForwardingLiveData(envId, flowId, authenticatedFetch) {
    return this.liveDataCall(envId, flowId, '', { method: 'DELETE' }, authenticatedFetch,
      'Failed to stop sending live data to your server');
  }

  /** Sends the owner's server the signed ping WhatsApp's health check makes. */
  async testLiveData(envId, flowId, authenticatedFetch) {
    return this.liveDataCall(envId, flowId, '/test', { method: 'POST' }, authenticatedFetch,
      'Failed to test your server');
  }

  async revealSigningSecret(envId, flowId, authenticatedFetch) {
    const body = await this.liveDataCall(envId, flowId, '/secret', { method: 'GET' }, authenticatedFetch,
      'Failed to load the signing secret');
    return body.secret;
  }

  async rotateSigningSecret(envId, flowId, authenticatedFetch) {
    const body = await this.liveDataCall(envId, flowId, '/secret', { method: 'POST' }, authenticatedFetch,
      'Failed to create a new signing secret');
    return body.secret;
  }

  async liveDataCall(envId, flowId, path, init, authenticatedFetch, fallback) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/live-data${path}`, init
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, fallback));
    }
    return await response.json();
  }

  /**
   * List the flow's keyword auto-send triggers.
   *
   * @param {string} envId Environment ID
   * @param {string} flowId Flow ID
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<Array<{id: string, flowId: string, triggerPhrase: string, matchType: string, source: string, bodyText: string|null, enabled: boolean}>>}
   */
  async listFlowTriggers(envId, flowId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/triggers`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to load the triggers'));
    }

    return await response.json();
  }

  /**
   * List every trigger in the environment (the flows page shows them on flow cards).
   *
   * @param {string} envId Environment ID
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<Array<{id: string, flowId: string, triggerPhrase: string, source: string, enabled: boolean}>>}
   */
  async listEnvironmentTriggers(envId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/triggers`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to load the triggers'));
    }

    return await response.json();
  }

  /**
   * Add a keyword auto-send trigger to a flow. When a matching message is seen, from a
   * customer (source CUSTOMER) or sent by the business itself (source BUSINESS), the flow
   * is automatically sent into that conversation.
   *
   * @param {string} envId Environment ID
   * @param {string} flowId Flow ID
   * @param {{triggerPhrase: string, matchType?: string, source?: string, bodyText?: string|null, enabled?: boolean}} trigger
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<object>} The created trigger
   */
  async createFlowTrigger(envId, flowId, trigger, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/triggers`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(trigger),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to save the trigger'));
    }

    return await response.json();
  }

  /**
   * Update a keyword auto-send trigger.
   *
   * @param {string} envId Environment ID
   * @param {string} flowId Flow ID
   * @param {string} triggerId Trigger ID
   * @param {{triggerPhrase: string, matchType?: string, source?: string, bodyText?: string|null, enabled?: boolean}} trigger
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<object>} The updated trigger
   */
  async updateFlowTrigger(envId, flowId, triggerId, trigger, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/triggers/${triggerId}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(trigger),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to save the trigger'));
    }

    return await response.json();
  }

  /**
   * Remove a keyword auto-send trigger.
   *
   * @param {string} envId Environment ID
   * @param {string} flowId Flow ID
   * @param {string} triggerId Trigger ID
   * @param {function} authenticatedFetch Authenticated fetch function
   */
  async deleteFlowTrigger(envId, flowId, triggerId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/triggers/${triggerId}`,
      { method: 'DELETE' }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to remove the trigger'));
    }
  }

  /**
   * Recent trigger decisions, the "why was this sent" log for keyword and intent triggers.
   *
   * @param {string} envId Environment ID
   * @param {string} flowId Flow ID
   * @param {number} [limit=20]
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<Array<{id: string, flowId: string, triggerId: string|null, contact: string, source: string, messageExcerpt: string|null, decision: string, confidence: number|null, model: string|null, latencyMs: number|null, createdAt: string}>>}
   */
  async listTriggerDecisions(envId, flowId, limit, authenticatedFetch) {
    const params = new URLSearchParams({ flowId, limit: String(limit || 20) });
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/trigger-decisions?${params.toString()}`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to load the decision log'));
    }

    return await response.json();
  }

  /**
   * Deprecate a published flow on WhatsApp.
   *
   * @param {string} envId Environment ID
   * @param {string} flowId Flow ID
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<object>} Deprecated flow
   */
  async deprecateFlow(envId, flowId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/${flowId}/deprecate`,
      { method: 'POST' }
    );

    if (response.status === 403) {
      throw new Error('Only organisation owners can deprecate flows');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to deprecate flow'));
    }

    return await response.json();
  }

  /**
   * List flows from WABA that can be imported.
   *
   * @param {string} envId Environment ID
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<Array>} List of importable flows with management status
   */
  async listImportableFlows(envId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/importable`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to list importable flows'));
    }

    return await response.json();
  }

  /**
   * Import a flow from WABA.
   *
   * @param {string} envId Environment ID
   * @param {string} whatsappFlowId WhatsApp Flow ID to import
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<object>} Imported flow
   */
  async importFlow(envId, whatsappFlowId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/import`,
      {
        method: 'POST',
        body: JSON.stringify({ whatsappFlowId })
      }
    );

    if (response.status === 403) {
      throw new Error('Only organisation owners can import flows');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to import flow'));
    }

    return await response.json();
  }

  /**
   * Test a data-exchange endpoint by probing reachability and (optionally)
   * the encrypted handshake.
   *
   * @param {string} envId Environment ID
   * @param {object} params {endpointUri, publicKeyPem}, publicKeyPem optional
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<object>} Test result
   *   {status, reachable, handshakeOk, httpStatus, latencyMs, message}
   */
  async testEndpoint(envId, { endpointUri, publicKeyPem }, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/test-endpoint`,
      {
        method: 'POST',
        body: JSON.stringify({ endpointUri, publicKeyPem })
      }
    );

    if (response.status === 403) {
      throw new Error('Only organisation owners can test endpoints');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to test endpoint'));
    }

    return await response.json();
  }

  /**
   * Sync flows from WhatsApp.
   *
   * @param {string} envId Environment ID
   * @param {function} authenticatedFetch Authenticated fetch function
   * @returns {Promise<object>} Sync response with statistics
   */
  async syncFlows(envId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/flows/sync`
    );

    if (!response.ok) {
      throw new Error('Failed to sync flows from WhatsApp');
    }

    return await response.json();
  }
}

export const flowService = new FlowService();
